import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { motion as Motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, Terminal, HelpCircle, ChevronRight, MessageSquare, Zap, Cpu, Award } from 'lucide-react';

const Background = lazy(() => import('./components/Background'));

// ROI Simulator Utility Calculations
const getBlueprintData = (ind, bot) => {
  const blueprints = {
    'lead-generation': {
      operations: {
        title: "Fully Automated Online Lead Generation",
        metricVal: "Always On",
        metricLbl: "Lead Capture & Qualification",
        metricVal2: "End to End",
        metricLbl2: "Automated Follow-Up",
        bullets: [
          "Fully automated SEO, AIO (AI search optimization), and social media lead generation workflows",
          "Capture, qualify, and route inbound leads into your CRM with automated follow-up sequences",
          "Connect campaign attribution and reporting to track which channels generate qualified inquiries"
        ]
      },
      engineering: {
        title: "Automated Lead Generation Infrastructure",
        metricVal: "Connected",
        metricLbl: "Search, Social & CRM",
        metricVal2: "Automated",
        metricLbl2: "Campaign Operations",
        bullets: [
          "Build fully automated SEO, AIO (AI search optimization), and social media acquisition pipelines",
          "Integrate landing pages, lead forms, enrichment, and CRM routing into one connected workflow",
          "Automate technical SEO checks, conversion tracking, and campaign performance reporting"
        ]
      },
      content: {
        title: "Automated Search & Social Content Engine",
        metricVal: "SEO + AIO",
        metricLbl: "Search Visibility Workflows",
        metricVal2: "Multi-Channel",
        metricLbl2: "Social Lead Generation",
        bullets: [
          "Fully automated keyword and audience research, SEO content production, and publishing",
          "AIO (AI search optimization) workflows structure useful content for AI-powered discovery",
          "Repurpose content for social media lead generation with automated scheduling, lead capture, and reporting"
        ]
      }
    },
    fintech: {
      operations: {
        title: "Secured Fintech Transaction Pipeline",
        metricVal: "Est. 14x Speed Boost",
        metricLbl: "Throughput Latency Cut",
        metricVal2: "99.8%",
        metricLbl2: "Transaction Accuracy",
        bullets: [
          "End-to-end Stripe integrations with zero-latency automated sales funnels",
          "Cryptographic payment verification and autonomic ledger settlements",
          "Edge-computed anti-fraud models running at less than 12ms per transaction"
        ]
      },
      engineering: {
        title: "Autonomous Financial Ledger System",
        metricVal: "94% Cost Cut",
        metricLbl: "Manual Audit Elimination",
        metricVal2: "< 50ms",
        metricLbl2: "State Sync Synchronization",
        bullets: [
          "DFS-driven algorithmic balance mapping across multi-currency protocols",
          "Automated ledger audit pipelines operating 100% autonomously without drift",
          "Cryptographically signed payment notifications and secure archival networks"
        ]
      },
      content: {
        title: "Generative Finance Report Engine",
        metricVal: "Est. 12x ROI Boost",
        metricLbl: "Report Synthesis Speed",
        metricVal2: "100%",
        metricLbl2: "GDPR Data Compliance",
        bullets: [
          "High-fidelity report generation driven by proprietary long-context neural mapping",
          "Autonomous synthesis of custom fintech dashboards with localized telemetry",
          "Secure sanitization pipelines to guarantee absolute transactional data privacy"
        ]
      }
    },
    saas: {
      operations: {
        title: "Enterprise SaaS Revenue Pipeline",
        metricVal: "Est. 18x Scaling Boost",
        metricLbl: "Revenue Funnel Throughput",
        metricVal2: "85%",
        metricLbl2: "Operations Latency Cut",
        bullets: [
          "Zero-latency automated billing and CRM workflow mapping",
          "Dynamic client onboarding flows triggered by autonomic backend state syncs",
          "Hyper-scalable Stripe funnels integrating automated multi-tier subscriptions"
        ]
      },
      engineering: {
        title: "Agentic SaaS Full-Stack Pipeline",
        metricVal: "Est. 10x ROI Boost",
        metricLbl: "Concept to Production Speed",
        metricVal2: "0%",
        metricLbl2: "Manual Engineering Bloat",
        bullets: [
          "100% agent-orchestrated rapid prototyping using automated DFS generation",
          "Self-healing backend deployment pipelines running on hyper-scalable edge layers",
          "Real-time diagnostic telemetry monitoring database connections autonomously"
        ]
      },
      content: {
        title: "SaaS Conversational Engine",
        metricVal: "98.2%",
        metricLbl: "Customer Resolution Accuracy",
        metricVal2: "Est. 8x Speed Boost",
        metricLbl2: "Client Intake Velocity",
        bullets: [
          "Persistent client memory states sustaining infinite user sessions autonomously",
          "Bespoke LLM swarms executing customer service diagnostics with deep research",
          "Self-optimizing FAQ vectors matching user requests with flawless context alignment"
        ]
      }
    },
    media: {
      operations: {
        title: "Multimodal Media Supply Chain",
        metricVal: "Est. 15x ROI Boost",
        metricLbl: "Publishing Velocity Speed",
        metricVal2: "0%",
        metricLbl2: "Manual Editing Tasks",
        bullets: [
          "Parallel multi-agent neural swarms executing automated research and synthesis",
          "State-of-the-art TTS generation featuring automated dynamic audio volume stitching",
          "Algorithmic content distribution running E2E from concept to multi-platform publishing"
        ]
      },
      engineering: {
        title: "Generative Multimodal R&D Lab",
        metricVal: "Est. 10x Scaling Boost",
        metricLbl: "Prototyping Pipeline Run",
        metricVal2: "60k Words",
        metricLbl2: "Synthesized Per Hour",
        bullets: [
          "Enterprise R&D long-context memory engines advancing generative prose architectures",
          "DFS procedural asset pipelines creating high-fidelity images and video frames",
          "Continuous testing environments evaluating narrative logic and visual consistency"
        ]
      },
      content: {
        title: "Bespoke Agentic Podcast Studio",
        metricVal: "Est. 14x Throughput",
        metricLbl: "Audio Generation Rate",
        metricVal2: "100%",
        metricLbl2: "Autonomously Engineered",
        bullets: [
          "Swarm-based podcast script writing and high-fidelity speech synthesis pipelines",
          "Precision audio engineering featuring automated volume matching and EQ presets",
          "Instant publishing loops deploying raw research topics directly to consumer feeds"
        ]
      }
    },
    gaming: {
      operations: {
        title: "Procedural Universe Generation",
        metricVal: "100% Procedural",
        metricLbl: "Runtime Environment Build",
        metricVal2: "< 30 mins",
        metricLbl2: "Concept to Production",
        bullets: [
          "Edge-computed DFS maze and level design algorithms executing in microseconds",
          "Procedural diffusion engines creating beautiful custom graphic assets on the fly",
          "Hyper-optimized lightweight runtime logic scaling seamlessly to infinite players"
        ]
      },
      engineering: {
        title: "Eulerian Graph Mechanics Lab",
        metricVal: "Est. 16x Retention Boost",
        metricLbl: "User Interaction Velocity",
        metricVal2: "Zero Delay",
        metricLbl2: "Procedural Asset Loading",
        bullets: [
          "Game dynamics built on Eulerian circuit graph theories for unique UX logic",
          "Hyper-scalable asset pipeline powered by edge-computed diffusion algorithms",
          "Direct Kotlin and Java engine compilers optimizing game latency for all devices"
        ]
      },
      content: {
        title: "Generative AI NPC Engine",
        metricVal: "100k Token",
        metricLbl: "NPC Memory Context Limit",
        metricVal2: "99.9%",
        metricLbl2: "Roleplay Coherency Level",
        bullets: [
          "Bespoke LLM NPCs retaining continuous memory states across game sessions",
          "Predictive user psychographic mapping to tailor dialogue dynamically on the fly",
          "Privacy-vaulted local processing guarantees complete client-side data anonymity"
        ]
      }
    }
  };

  const selectedIndustry = blueprints[ind] || blueprints.saas;
  return selectedIndustry[bot] || selectedIndustry.operations || selectedIndustry.engineering;
};

function App() {
  const [showBackground, setShowBackground] = useState(false);

  // Interactive ROI Simulator State
  const [simulatorIndustry, setSimulatorIndustry] = useState('saas');
  const [simulatorBottleneck, setSimulatorBottleneck] = useState('engineering');

  // Client Inquiry Form State
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientOrg, setClientOrg] = useState('');
  const [clientBudget, setClientBudget] = useState('$50,000 - $200,000');
  const [clientScope, setClientScope] = useState('');
  const [inquiryBlueprint, setInquiryBlueprint] = useState('');
  const [inquirySelection, setInquirySelection] = useState(null);
  const [clientWebsite, setClientWebsite] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [captchaChallenge, setCaptchaChallenge] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaExpired, setCaptchaExpired] = useState(false);
  const [captchaSubmitted, setCaptchaSubmitted] = useState(false);
  const [isCaptchaLoading, setIsCaptchaLoading] = useState(false);
  const [captchaError, setCaptchaError] = useState('');
  const [captchaBlocked, setCaptchaBlocked] = useState(false);
  const [isCaptchaOpen, setIsCaptchaOpen] = useState(false);
  const captchaRequestRef = useRef(null);
  const captchaDialogRef = useRef(null);
  const captchaTitleRef = useRef(null);
  const captchaAnswerRef = useRef(null);
  const captchaLoadRef = useRef(null);
  const captchaSendRef = useRef(null);
  const captchaReturnFocusRef = useRef(null);
  const pendingLeadRef = useRef(null);
  const submissionRef = useRef(null);
  const needsFreshCaptcha = Boolean(captchaChallenge) && captchaExpired && !captchaSubmitted;

  useEffect(() => {
    const timer = window.setTimeout(() => setShowBackground(true), 500);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!captchaChallenge) return;
    const delay = Math.max(0, Date.parse(captchaChallenge.expiresAt) - Date.now());
    const timer = window.setTimeout(() => setCaptchaExpired(true), delay);
    return () => window.clearTimeout(timer);
  }, [captchaChallenge]);

  useEffect(() => {
    const dialog = captchaDialogRef.current;
    if (!isCaptchaOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (!dialog.open) dialog.showModal();
    captchaTitleRef.current?.focus();
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      if (captchaReturnFocusRef.current?.isConnected) captchaReturnFocusRef.current.focus();
    };
  }, [isCaptchaOpen]);

  useEffect(() => {
    if (!isCaptchaOpen || isCaptchaLoading || isSubmitting) return;
    if (captchaChallenge && !needsFreshCaptcha) {
      if (captchaSubmitted) captchaSendRef.current?.focus();
      else captchaAnswerRef.current?.focus();
    } else if (captchaError || needsFreshCaptcha) {
      if (captchaBlocked) captchaTitleRef.current?.focus();
      else captchaLoadRef.current?.focus();
    }
  }, [isCaptchaOpen, captchaChallenge, isCaptchaLoading, isSubmitting, captchaSubmitted, needsFreshCaptcha, captchaError, captchaBlocked]);

  useEffect(() => {
    return () => captchaRequestRef.current?.abort();
  }, []);

  const loadCaptcha = async () => {
    if (captchaRequestRef.current || submissionRef.current?.inFlight) return;
    const controller = new AbortController();
    captchaRequestRef.current = controller;
    setIsCaptchaLoading(true);
    setCaptchaError('');
    const timeout = window.setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch('/api/captcha', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal: controller.signal,
      });
      const result = await response.json().catch(() => null);
      if (captchaRequestRef.current !== controller) return;
      if (result?.code === 'DAILY_LIMIT' || result?.code === 'IP_BANNED') {
        setCaptchaBlocked(true);
        setCaptchaChallenge(null);
        setCaptchaAnswer('');
        setCaptchaError('The maths check is unavailable. Please try again later or email CC-LAB.');
        return;
      }
      if (!response.ok || typeof result?.captchaId !== 'string'
        || typeof result?.imageDataUrl !== 'string' || !result.imageDataUrl.startsWith('data:image/')
        || !Number.isFinite(Date.parse(result?.expiresAt))) {
        throw new Error('The maths check could not be loaded. Please try again.');
      }
      setCaptchaChallenge({
        captchaId: result.captchaId,
        imageDataUrl: result.imageDataUrl,
        expiresAt: result.expiresAt,
      });
      submissionRef.current = null;
      setCaptchaAnswer('');
      setCaptchaExpired(false);
      setCaptchaSubmitted(false);
    } catch (error) {
      if (captchaRequestRef.current !== controller) return;
      setCaptchaError(error.name === 'AbortError'
        ? 'Loading the maths check timed out. Please try again.'
        : 'The maths check could not be loaded. Check your connection and try again.');
    } finally {
      window.clearTimeout(timeout);
      if (captchaRequestRef.current === controller) {
        captchaRequestRef.current = null;
        setIsCaptchaLoading(false);
      }
    }
  };

  const closeCaptcha = () => {
    if (submissionRef.current?.inFlight) return;
    const controller = captchaRequestRef.current;
    captchaRequestRef.current = null;
    controller?.abort();
    setIsCaptchaLoading(false);
    setIsCaptchaOpen(false);
    if (submissionRef.current?.retryable) {
      setFormError('Your inquiry has not been confirmed. Send it again to retry with the same details.');
    }
  };

  const handleScrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -90; // Navbar gap offset
      const rect = element.getBoundingClientRect();
      const targetY = rect.top + window.scrollY + yOffset;
      window.scrollTo({ top: targetY, behavior: 'smooth' });
    }
  };

  // Autonomously populate the consultation form based on the ROI blueprint setup
  const handleDeployBlueprint = () => {
    const blueprint = getBlueprintData(simulatorIndustry, simulatorBottleneck);
    const scopeMsg = `Requesting custom development pipeline for: ${blueprint.title}.
Primary goals: 
- Reduce bottleneck issues relating to "${simulatorBottleneck === 'operations' ? 'Operations Latency' : simulatorBottleneck === 'engineering' ? 'Manual Engineering Pipelines' : 'Content Generation Scalability'}".
- Target metric thresholds: ${blueprint.metricVal} and ${blueprint.metricVal2}.`;

    setClientScope(scopeMsg);
    setInquiryBlueprint(blueprint.title);
    setInquirySelection({ industry: simulatorIndustry, bottleneck: simulatorBottleneck });
    handleScrollToSection('inquiry');
  };

  const handleInquirySubmit = (e) => {
    e.preventDefault();
    if (submissionRef.current?.inFlight || captchaRequestRef.current || isCaptchaOpen) return;
    if (!e.currentTarget.reportValidity()) return;
    if (!clientName.trim() || !clientEmail.trim()) {
      setFormError('Please enter your name and email address.');
      return;
    }
    const lead = {
      name: clientName.trim(),
      email: clientEmail.trim(),
      organization: clientOrg.trim(),
      budget: clientBudget,
      scope: clientScope.trim(),
      blueprint: inquiryBlueprint.slice(0, 200),
      industry: inquirySelection?.industry || simulatorIndustry,
      bottleneck: inquirySelection?.bottleneck || simulatorBottleneck,
      website: clientWebsite,
    };
    const leadPayload = JSON.stringify(lead);
    const canRetry = submissionRef.current?.retryable && submissionRef.current.leadPayload === leadPayload;
    pendingLeadRef.current = Object.freeze(lead);
    captchaReturnFocusRef.current = document.activeElement;
    setFormError('');
    setIsCaptchaOpen(true);
    if (canRetry) return;

    submissionRef.current = null;
    setCaptchaChallenge(null);
    setCaptchaAnswer('');
    setCaptchaExpired(false);
    setCaptchaSubmitted(false);
    setCaptchaBlocked(false);
    setCaptchaError('');
    void loadCaptcha();
  };

  const handleCaptchaSubmit = async (e) => {
    e.preventDefault();
    if (submissionRef.current?.inFlight || captchaRequestRef.current || captchaBlocked) return;
    if (!pendingLeadRef.current || !captchaChallenge) return;
    if (!captchaSubmitted && Date.parse(captchaChallenge.expiresAt) <= Date.now()) {
      setCaptchaExpired(true);
      setCaptchaError('The maths check has expired. Get a new challenge before sending your inquiry.');
      return;
    }
    if (!/^\d{1,3}$/.test(captchaAnswer.trim())) {
      setCaptchaError('Enter the numerical answer shown in the image.');
      return;
    }

    let submission = submissionRef.current;
    if (!submission) {
      submission = {
        leadPayload: JSON.stringify(pendingLeadRef.current),
        body: JSON.stringify({
          ...pendingLeadRef.current,
          captchaId: captchaChallenge.captchaId,
          captchaAnswer: captchaAnswer.trim(),
          submissionId: crypto.randomUUID(),
        }),
        retryable: true,
      };
      submissionRef.current = submission;
    }
    submission.inFlight = true;
    setCaptchaSubmitted(true);
    setIsSubmitting(true);
    setCaptchaError('');
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 25000);

    try {
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: submission.body,
        signal: controller.signal,
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || result?.success !== true) {
        if (result?.code === 'DAILY_LIMIT' || result?.code === 'IP_BANNED') {
          submissionRef.current = null;
          setCaptchaBlocked(true);
          setCaptchaChallenge(null);
          setCaptchaAnswer('');
          setCaptchaError('The maths check is unavailable. Please try again later or email CC-LAB.');
          return;
        }
        const captchaMessages = {
          CAPTCHA_REQUIRED: 'Please complete a new maths check before sending your inquiry.',
          CAPTCHA_INCORRECT: 'That answer was incorrect. Get a new challenge and try again.',
          CAPTCHA_EXPIRED: 'The maths check has expired. Get a new challenge and try again.',
          CAPTCHA_USED: 'This maths check has already been used. Get a new challenge and try again.',
          CAPTCHA_IP_MISMATCH: 'Your connection changed. Get a new challenge and try again.',
        };
        if (captchaMessages[result?.code] || result?.captchaRequired === true) {
          submissionRef.current = null;
          setCaptchaChallenge(null);
          setCaptchaAnswer('');
          setCaptchaSubmitted(false);
          setCaptchaError(captchaMessages[result?.code] || 'Please complete a new maths check before trying again.');
          return;
        }
        if (response.status === 429) {
          throw new Error('Please wait a moment before retrying your inquiry.');
        }
        if (response.status === 400) {
          throw new Error('Please check your inquiry details and try again.');
        }
        throw new Error('We could not confirm your inquiry was sent. Please try again.');
      }

      submission.retryable = false;
      setIsCaptchaOpen(false);
      setFormSubmitted(true);
    } catch (error) {
      setCaptchaError(error.name === 'AbortError'
        ? 'The request timed out. Please retry to confirm your inquiry was sent.'
        : error instanceof TypeError
          ? 'We could not connect. Check your internet connection and try again.'
          : error.message);
    } finally {
      window.clearTimeout(timeout);
      submission.inFlight = false;
      setIsSubmitting(false);
    }
  };

  const activeBlueprint = getBlueprintData(simulatorIndustry, simulatorBottleneck);

  return (
    <div 
      className="app-main"
      style={{ paddingTop: '80px' }} // Spacing for floating navbar
    >
      {showBackground && (
        <Suspense fallback={null}>
          <Background />
        </Suspense>
      )}

      {/* Floating Glassmorphic Navigation Bar */}
      <nav className="nav-container">
        <div className="nav-bar">
          <a href="#" className="nav-brand" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <img src="/cc-lab-logo-transparent-bg-160.webp" alt="CC-LAB logo" className="nav-brand-logo" width="38" height="38" />
            <span className="nav-brand-text">CC-LAB</span>
          </a>

          <div className="nav-links">
            <a href="#capabilities" className="nav-link" onClick={(e) => { e.preventDefault(); handleScrollToSection('capabilities'); }}>
              Capabilities
            </a>
            <a href="#roi-blueprint" className="nav-link" onClick={(e) => { e.preventDefault(); handleScrollToSection('roi-blueprint'); }}>
              ROI Simulator
            </a>
            <a href="#methodology" className="nav-link" onClick={(e) => { e.preventDefault(); handleScrollToSection('methodology'); }}>
              Methodology
            </a>
            <button className="nav-btn" onClick={() => handleScrollToSection('inquiry')}>
              Direct Inquiry
            </button>
          </div>
        </div>
      </nav>

      {/* Custom Consulting Hero Redesign */}
      <header className="hero-section">
        <Motion.div
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
          <div className="consulting-badge">
            <span className="pulse-dot"></span>
            SOTA AI Consultancy & Deep Tech R&D
          </div>

          <h1 className="hero-headline">
            <span className="headline-white">We Engineer</span>
            <span className="headline-gradient">Autonomous Intelligence</span>
          </h1>

          <p className="hero-lead">
            CC-LAB is an elite engineering lab designing and deploying bespoke <strong>LLM memory architectures</strong>, high-performance <strong>multi-agent swarms</strong>, and automated fintech pipelines to resolve enterprise operational bottlenecks and accelerate revenue.
          </p>

          <div className="hero-ctas">
            <button className="btn-primary" onClick={() => handleScrollToSection('roi-blueprint')}>
              Build ROI Blueprint <ChevronRight size={16} />
            </button>
            <button className="btn-secondary" onClick={() => handleScrollToSection('methodology')}>
              Explore Methodology
            </button>
          </div>
        </Motion.div>
      </header>

      {/* Live Capability Ticker */}
      <section className="ticker-container" id="capabilities">
        <div className="ticker-track">
          {[...Array(2)].map((_, trackIdx) => (
            <React.Fragment key={trackIdx}>
              <div className="ticker-item">
                <Cpu size={14} /> Persistent Memory Architectures <span className="highlight">(100k+ tokens)</span>
              </div>
              <div className="ticker-dot"></div>
              <div className="ticker-item">
                <Zap size={14} /> Neural Agentic Swarms <span className="highlight">(Multi-Agent Ops)</span>
              </div>
              <div className="ticker-dot"></div>
              <div className="ticker-item">
                <Award size={14} /> E2E Revenue Automation <span className="highlight">(Fintech Integration)</span>
              </div>
              <div className="ticker-dot"></div>
              <div className="ticker-item">
                <Cpu size={14} /> Algorithmic Level Prototyping <span className="highlight">(DFS Procedural)</span>
              </div>
              <div className="ticker-dot"></div>
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* Interactive AI ROI Blueprint Simulator */}
      <section className="blueprint-section" id="roi-blueprint">
        <span className="section-pretitle">ROI Simulator</span>
        <h2 className="section-title">Design Your Custom AI Blueprint</h2>
        <p className="section-description">
          Toggle our core capabilities and target domains below to generate a tailored solution blueprint mapping estimated ROI thresholds, custom neural systems, and integration vectors.
        </p>

        <Motion.div 
          className="simulator-container"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="simulator-controls">
            <div>
              <h3 className="control-group-title">
                <span className="control-number">1</span> Choose Industry Segment
              </h3>
              <div className="selector-grid">
                {[
                  { id: 'saas', title: 'SaaS & Enterprise', desc: 'Agentic CRM & Workflows' },
                  { id: 'fintech', title: 'Fintech & Audit', desc: 'Secure payment engines' },
                  { id: 'media', title: 'Media & Podcasts', desc: 'TTS swarms & narrative IP' },
                  { id: 'gaming', title: 'Gaming & Simulation', desc: 'Procedural DFS worlds' },
                  { id: 'lead-generation', title: 'Online Lead Generation', desc: 'Fully automated SEO, AIO & social media' }
                ].map((ind) => (
                  <button
                    key={ind.id}
                    className={`selector-btn ${simulatorIndustry === ind.id ? 'selected' : ''}`}
                    onClick={() => setSimulatorIndustry(ind.id)}
                    aria-pressed={simulatorIndustry === ind.id}
                    disabled={isSubmitting}
                  >
                    <span className="btn-title">{ind.title}</span>
                    <span className="btn-desc">{ind.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="control-group-title">
                <span className="control-number">2</span> Pinpoint Core Bottleneck
              </h3>
              <div className="selector-grid">
                {[
                  { id: 'operations', title: 'Operational Latency', desc: 'Manual overhead & queues' },
                  { id: 'engineering', title: 'Engineering Velocity', desc: 'Slow prototyping cycles' },
                  { id: 'content', title: 'Content Scalability', desc: 'Static media pipelines' }
                ].map((bot) => (
                  <button
                    key={bot.id}
                    className={`selector-btn ${simulatorBottleneck === bot.id ? 'selected' : ''}`}
                    onClick={() => setSimulatorBottleneck(bot.id)}
                    aria-pressed={simulatorBottleneck === bot.id}
                    disabled={isSubmitting}
                  >
                    <span className="btn-title">{bot.title}</span>
                    <span className="btn-desc">{bot.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="simulator-display">
            <div className="display-header">
              <span className="display-tag">Generated Architecture Blueprint</span>
              <h4 className="display-title">{activeBlueprint.title}</h4>
            </div>

            <div className="display-metrics">
              <div className="metric-card">
                <span className="metric-val">{activeBlueprint.metricVal}</span>
                <span className="metric-lbl">{activeBlueprint.metricLbl}</span>
              </div>
              <div className="metric-card">
                <span className="metric-val">{activeBlueprint.metricVal2}</span>
                <span className="metric-lbl">{activeBlueprint.metricLbl2}</span>
              </div>
            </div>

            <div className="blueprint-details">
              <span className="blueprint-details-title">Key Integration Vectors:</span>
              <ul className="blueprint-bullets">
                {activeBlueprint.bullets.map((bullet, idx) => (
                  <li key={idx} className="blueprint-bullet">{bullet}</li>
                ))}
              </ul>
            </div>

            <button className="blueprint-action-btn" onClick={handleDeployBlueprint} disabled={isSubmitting}>
              Request This Setup <MessageSquare size={14} />
            </button>
          </div>
        </Motion.div>
      </section>

      {/* Engagement Methodology Timeline */}
      <section className="methodology-section" id="methodology">
        <span className="section-pretitle">Engagement Model</span>
        <h2 className="section-title">Our Autonomic Development Loop</h2>
        <p className="section-description">
          We leverage hyper-rapid agentic workflows and precise manual oversight to go from raw technical telemetry diagnosis directly to high-capacity live deployments.
        </p>

        <div className="timeline-container">
          {[
            { step: '01', title: 'Synthesis & Diagnosis', desc: 'We ingest your technical database models, APIs, and operational performance statistics to catalog key systemic friction points.' },
            { step: '02', title: 'Rapid Prototyping', desc: 'Deploying custom neural DFS generator scripts to build fully functioning, visual prototype architectures in less than 30 minutes.' },
            { step: '03', title: 'Neural Swarm Integration', desc: 'Orchestrating specialized AI agent clusters powered by persistent memory states (100k+ tokens) to manage non-linear operations.' },
            { step: '04', title: 'Autonomic Production Run', desc: 'Deploying the finalized framework onto high-efficiency edge servers with integrated self-healing telemetry monitors.' }
          ].map((node, idx) => (
            <Motion.div 
              className="timeline-step" 
              key={idx}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="step-node">
                <span className="step-number">{node.step}</span>
              </div>
              <div className="step-content">
                <h3 className="step-title">{node.title}</h3>
                <p className="step-desc">{node.desc}</p>
              </div>
            </Motion.div>
          ))}
        </div>
      </section>

      {/* Client Intake Form Section */}
      <section className="intake-section" id="inquiry">
        <span className="section-pretitle">Staging Intake</span>
        <h2 className="section-title">Initialize System Synthesis</h2>
        <p className="section-description">
          Partner with CC-LAB to automate manual bottlenecks or deploy autonomous agent frameworks inside your technology stack. Submit your system blueprint inquiry below.
        </p>

        <Motion.div 
          className="intake-form-container"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <AnimatePresence mode="wait">
            {!formSubmitted ? (
              <Motion.form 
                key="intake-form"
                className="intake-form"
                onSubmit={handleInquirySubmit}
                aria-busy={isSubmitting}
                aria-describedby={formError ? 'inquiry-error' : undefined}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="form-field">
                  <label htmlFor="client-name" className="form-label">Full Name</label>
                  <input 
                    id="client-name"
                    type="text" 
                    className="form-input" 
                    required 
                    maxLength={120}
                    autoComplete="name"
                    disabled={isSubmitting}
                    placeholder="e.g. Satoshi Nakamoto" 
                    value={clientName} 
                    onChange={(e) => setClientName(e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="client-email" className="form-label">Email Address</label>
                  <input 
                    id="client-email"
                    type="email" 
                    className="form-input" 
                    required 
                    maxLength={254}
                    autoComplete="email"
                    disabled={isSubmitting}
                    placeholder="e.g. client@domain.com" 
                    value={clientEmail} 
                    onChange={(e) => setClientEmail(e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="client-org" className="form-label">Organization Name</label>
                  <input 
                    id="client-org"
                    type="text" 
                    className="form-input" 
                    maxLength={200}
                    autoComplete="organization"
                    disabled={isSubmitting}
                    placeholder="e.g. Sovereign Systems Corp" 
                    value={clientOrg} 
                    onChange={(e) => setClientOrg(e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="client-budget" className="form-label">Estimated Project Budget</label>
                  <select 
                    id="client-budget"
                    className="form-select"
                    disabled={isSubmitting}
                    value={clientBudget}
                    onChange={(e) => setClientBudget(e.target.value)}
                  >
                    <option>$10,000 - $50,000</option>
                    <option>$50,000 - $200,000</option>
                    <option>$200,000 - $500,000</option>
                    <option>$500,000+</option>
                  </select>
                </div>
                <div className="form-field form-full-width">
                  <label htmlFor="client-scope" className="form-label">Technical Project Scope & System Bottlenecks</label>
                  <textarea 
                    id="client-scope"
                    className="form-textarea" 
                    maxLength={6000}
                    disabled={isSubmitting}
                    placeholder="Provide details about your current infrastructure architecture and where manual processes or latency bottlenecks limit your operational scale..."
                    value={clientScope}
                    onChange={(e) => setClientScope(e.target.value)}
                  />
                </div>

                <div className="form-honeypot" aria-hidden="true">
                  <label htmlFor="client-website">Leave this field empty</label>
                  <input
                    id="client-website"
                    name="website"
                    type="text"
                    autoComplete="off"
                    tabIndex={-1}
                    disabled={isSubmitting}
                    value={clientWebsite}
                    onChange={(e) => setClientWebsite(e.target.value)}
                  />
                </div>

                {formError && (
                  <p id="inquiry-error" className="form-error form-full-width" role="alert">
                    {formError} Your details are still in the form.
                  </p>
                )}

                <div className="submit-button-container">
                  <button 
                    type="submit" 
                    className="submit-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Terminal size={16} className="pulse-icon" aria-hidden="true" /> Sending Inquiry...
                      </>
                    ) : (
                      <>
                        {formError ? 'Retry Sending Inquiry' : 'Send Inquiry'} <ChevronRight size={16} aria-hidden="true" />
                      </>
                    )}
                  </button>
                </div>
              </Motion.form>
            ) : (
              <Motion.div 
                key="success-card"
                className="intake-success-card"
                role="status"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 100 }}
              >
                <div className="success-icon-box">
                  <CheckCircle size={36} />
                </div>
                <h3 className="success-title">Inquiry Sent</h3>
                <p className="success-text">
                  Your inquiry has been sent to CC-LAB. We will reply to {clientEmail} to discuss your project.
                </p>

                <div className="terminal-status-card">
                  <div className="terminal-line">
                    &gt; Name: <span className="white">{clientName}</span>
                  </div>
                  <div className="terminal-line">
                    &gt; Organization: <span className="white">{clientOrg || 'Not provided'}</span>
                  </div>
                  {inquiryBlueprint && (
                    <div className="terminal-line">
                      &gt; Targeting Blueprint: <span className="white">"{inquiryBlueprint}"</span>
                    </div>
                  )}
                  <div className="terminal-line">
                    &gt; Estimated Budget: <span className="white">{clientBudget}</span>
                  </div>
                  <div className="terminal-line">
                    &gt; Inquiry Status: <span className="green">SENT</span>
                  </div>
                </div>
              </Motion.div>
            )}
          </AnimatePresence>
        </Motion.div>
      </section>

      <dialog
        ref={captchaDialogRef}
        className="captcha-dialog"
        aria-labelledby="captcha-dialog-title"
        aria-describedby="captcha-instructions"
        onCancel={(event) => { event.preventDefault(); closeCaptcha(); }}
      >
        <form className="captcha-dialog-form" onSubmit={handleCaptchaSubmit} aria-busy={isCaptchaLoading || isSubmitting}>
          <div className="captcha-dialog-header">
            <h3 id="captcha-dialog-title" ref={captchaTitleRef} tabIndex={-1}>Maths check</h3>
            <button className="captcha-close-button" type="button" aria-label="Close maths check" onClick={closeCaptcha} disabled={isSubmitting}>
              <X size={20} aria-hidden="true" />
            </button>
          </div>
          <p className="captcha-help" id="captcha-instructions">Enter the result shown in the image to send your inquiry.</p>
          {isCaptchaLoading ? (
            <p className="captcha-help" role="status">Loading maths check...</p>
          ) : captchaChallenge && !captchaBlocked && !needsFreshCaptcha ? (
            <>
              <img
                className="captcha-image"
                src={captchaChallenge.imageDataUrl}
                alt="Maths challenge. Enter the result below."
                onError={() => {
                  if (captchaSubmitted) return;
                  setCaptchaChallenge(null);
                  setCaptchaAnswer('');
                  setCaptchaError('The maths check image could not be displayed. Please load it again.');
                }}
              />
              <label className="form-label" htmlFor="captcha-answer">Your answer</label>
              <input
                id="captcha-answer"
                ref={captchaAnswerRef}
                className="form-input captcha-answer"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{1,3}"
                maxLength={3}
                autoComplete="off"
                required
                readOnly={captchaSubmitted}
                value={captchaAnswer}
                onChange={(event) => setCaptchaAnswer(event.target.value)}
                disabled={isSubmitting}
                aria-describedby={`captcha-instructions${captchaError ? ' captcha-error' : ''}`}
              />
            </>
          ) : !captchaBlocked && (
            <button ref={captchaLoadRef} className="captcha-load-button" type="button" onClick={loadCaptcha} disabled={isSubmitting}>Get new challenge</button>
          )}
          {needsFreshCaptcha && !captchaBlocked && !isCaptchaLoading && (
            <p className="captcha-help" role="status">This maths check has expired. Get a new challenge to continue.</p>
          )}
          {captchaError && <p className="form-error" id="captcha-error" role="alert">{captchaError}</p>}
          <div className="captcha-dialog-actions">
            <button className="captcha-load-button" type="button" onClick={closeCaptcha} disabled={isSubmitting}>Cancel</button>
            <button
              ref={captchaSendRef}
              className="submit-button"
              type="submit"
              disabled={isSubmitting || isCaptchaLoading || captchaBlocked || !captchaChallenge || needsFreshCaptcha}
            >
              {isSubmitting ? 'Sending inquiry...' : captchaSubmitted ? 'Retry sending' : 'Verify & send'}
            </button>
          </div>
          <p className="captcha-help">Need help? <a className="captcha-help-link" href="mailto:cc@cc-lab.xyz">Email CC-LAB</a>.</p>
        </form>
      </dialog>

      <footer>
        <div style={{ marginBottom: '60px', opacity: 0.8 }}>
          <p style={{ marginBottom: '20px', letterSpacing: '4px', color: 'var(--accent-blue)', fontWeight: 'bold' }}>Strategic Partnerships:</p>
          <p style={{ marginBottom: '20px', letterSpacing: '1px', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>Direct Inquiry: <a href="mailto:contact@cc-lab.xyz" style={{ color: 'var(--accent-blue)', textDecoration: 'none' }}>contact@cc-lab.xyz</a></p>
          <div className="footer-logo-container">
            <div className="logo-box square">
              <img src="/cc-lab-logo-square-white-bg.webp" alt="Square Logo" width="100" height="100" style={{ maxWidth: '100%', maxHeight: '100%' }} loading="lazy" />
            </div>
            <div className="logo-box text">
              <img src="/cc-lab-logo-textonly-white-bg.webp" alt="Text Only Logo" width="200" height="60" style={{ maxWidth: '100%', maxHeight: '100%' }} loading="lazy" />
            </div>
            <div className="logo-box transparent">
              <img
                src="/cc-lab-logo-transparent-bg.webp"
                srcSet="/cc-lab-logo-transparent-bg-160.webp 160w, /cc-lab-logo-transparent-bg-280.webp 280w, /cc-lab-logo-transparent-bg-400.webp 400w"
                sizes="130px"
                alt="White Version"
                className="footer-transparent-logo"
                width="130"
                height="130"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>

          <div className="footer-social-links">
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="social-link">
              LINKEDIN
            </a>
            <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="social-link">
              GITHUB
            </a>
          </div>
        </div>

        <p className="footer-legal">© 2026 CC-LAB.XYZ | PROTOCOL INITIATED | SECURED ENVIRONMENT</p>
      </footer>
    </div>
  );
}

export default App;
